// Pure, client-safe parser for external video links. The public page only ever
// embeds the normalized `embedUrl` this returns, never the raw admin input, so
// the iframe src is always one of two known player origins.

export type VideoEmbedProvider = "youtube" | "vimeo";

export interface VideoEmbed {
  provider: VideoEmbedProvider;
  embedUrl: string;
  /** Provider thumbnail when derivable without a network call (YouTube only). */
  posterUrl: string | null;
}

const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;
const VIMEO_ID = /^\d{6,12}$/;
/** Privacy hash of an unlisted Vimeo video; the player refuses to load it without `?h=`. */
const VIMEO_HASH = /^[A-Za-z0-9]{1,32}$/;
/** `/embed/videoseries?list=...` is a playlist player; playlists are not supported. */
const YOUTUBE_RESERVED_PATHS = new Set(["videoseries"]);

function youtubeId(url: URL): string | null {
  const host = url.hostname.replace(/^www\.|^m\./, "");
  if (host === "youtu.be") return url.pathname.slice(1).split("/")[0] || null;
  if (host !== "youtube.com" && host !== "youtube-nocookie.com") return null;
  const v = url.searchParams.get("v");
  if (v) return v;
  const m = /^\/(?:shorts|embed|live|v)\/([^/?]+)/.exec(url.pathname);
  const id = m?.[1] ?? null;
  // Recognized as YouTube but not a single video: "" fails YOUTUBE_ID, so the link is rejected.
  if (id !== null && YOUTUBE_RESERVED_PATHS.has(id)) return "";
  return id;
}

function vimeoId(url: URL): { id: string; hash: string | null } | null {
  const host = url.hostname.replace(/^www\./, "");
  if (host !== "vimeo.com" && host !== "player.vimeo.com") return null;
  // vimeo.com/<id>, vimeo.com/<id>/<hash> (unlisted share link), player.vimeo.com/video/<id>?h=<hash>
  const m = /^\/(?:video\/)?(\d+)(?:\/([^/?#]+))?/.exec(url.pathname);
  if (!m?.[1]) return null;
  const hash = url.searchParams.get("h") ?? m[2] ?? null;
  return { id: m[1], hash: hash && VIMEO_HASH.test(hash) ? hash : null };
}

export function parseVideoEmbed(input: string): VideoEmbed | null {
  let url: URL;
  try {
    url = new URL(input.trim());
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;

  const yt = youtubeId(url);
  if (yt !== null) {
    if (!YOUTUBE_ID.test(yt)) return null;
    return {
      provider: "youtube",
      embedUrl: `https://www.youtube-nocookie.com/embed/${yt}`,
      posterUrl: `https://img.youtube.com/vi/${yt}/hqdefault.jpg`,
    };
  }

  const vm = vimeoId(url);
  if (vm !== null) {
    if (!VIMEO_ID.test(vm.id)) return null;
    const query = vm.hash ? `?h=${vm.hash}` : "";
    return { provider: "vimeo", embedUrl: `https://player.vimeo.com/video/${vm.id}${query}`, posterUrl: null };
  }

  return null;
}

/** Adds `autoplay=1`; an unlisted Vimeo `embedUrl` already carries `?h=`, so a bare `?` would break it. */
export function embedUrlWithAutoplay(embedUrl: string): string {
  return `${embedUrl}${embedUrl.includes("?") ? "&" : "?"}autoplay=1`;
}

const YOUTUBE_THUMBNAIL = /^https:\/\/img\.youtube\.com\/vi\/[A-Za-z0-9_-]{11}\/hqdefault\.jpg$/;
/** Frame grabbed by `describeUploadedVideo` (src/api/videos/poster.ts) next to the upload. */
const UPLOAD_FRAME_POSTER = /^\/uploads\/videos\/[A-Za-z0-9-]+\/poster\.jpg$/;

/**
 * True when a stored poster was derived from the video source (YouTube
 * thumbnail or ffmpeg frame) rather than chosen by an admin. Explicit posters
 * are uploaded to the `video-posters` folder, so they never match. Such a
 * poster belongs to its source and must be re-derived when the source changes.
 */
export function isAutoDerivedPoster(posterUrl: string | null | undefined): boolean {
  if (!posterUrl) return false;
  return YOUTUBE_THUMBNAIL.test(posterUrl) || UPLOAD_FRAME_POSTER.test(posterUrl);
}

/** "3:05" / "1:02:09" for card badges. */
export function formatVideoDuration(sec: number | null | undefined): string | null {
  if (sec == null || !Number.isFinite(sec) || sec <= 0) return null;
  const total = Math.round(sec);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = h > 0 ? String(m).padStart(2, "0") : String(m);
  return `${h > 0 ? `${h}:` : ""}${mm}:${String(s).padStart(2, "0")}`;
}
